<div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
  <div>
    <h5 class="mb-1 fw-bold">Product Transfers</h5>
    <span class="sub-text">One consignment out of the factory go-down, carrying any number of product batches</span>
  </div>
  <button class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2" (click)="openExportModal()">
    <i class="bi bi-download"></i>
    Export Report
  </button>
</div>

<div class="alert alert-info d-flex align-items-start mb-3" role="alert">
  <i class="bi bi-info-circle-fill me-2 mt-1"></i>
  <div>
    <strong>Transfers cannot be edited, and a single product cannot be removed from one.</strong>
    Deleting is the only correction, and it reverses every product on the transfer at once: the units go back to the
    factory go-down, and a warehouse transfer also comes off that warehouse. Delete the whole consignment and enter it
    again. Once the destination warehouse has dispatched or moved those units on, the transfer can no longer be reversed
    and the record has to stand.
  </div>
</div>

<div class="row mb-4 g-2">
  <div class="col-12 col-md-2 ps-0">
    <ng-select
      placeholder="All Products"
      [items]="products"
      [(ngModel)]="selectedFilterProduct"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="true"
      (ngModelChange)="onFilterProductChange()" />
  </div>

  <div class="col-12 col-md-3">
    <ng-select
      placeholder="All Dealers"
      [items]="dealers"
      [(ngModel)]="selectedFilterDealer"
      bindValue="id"
      bindLabel="shop_name"
      [clearable]="true"
      [searchable]="true"
      (ngModelChange)="onFilterDealerChange()" />
  </div>

  <div class="col-12 col-md-3">
    <ng-select
      placeholder="All Factories"
      [items]="factoryList"
      [(ngModel)]="selectedFilterFactory"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="false"
      (ngModelChange)="onFilterFactoryChange()" />
  </div>

  <div class="col-12 col-md-2">
    <ng-select
      placeholder="All Warehouses"
      [items]="warehouses"
      [(ngModel)]="selectedFilterWarehouse"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="false"
      (ngModelChange)="onFilterWarehouseChange()" />
  </div>

  <div class="col-12 col-md-2 pe-0">
    <button class="btn btn-primary d-flex align-items-center gap-2 w-100" (click)="openTransferModal()">
      <i class="bi bi-arrow-left-right"></i>
      New Transfer
    </button>
  </div>
</div>

@if (blockedBatchNumber) {
  <div class="alert alert-warning d-flex align-items-start mb-3" role="alert">
    <i class="bi bi-signpost-split-fill me-2 mt-1"></i>
    <div class="flex-grow-1">
      <strong>Clearing batch {{ blockedBatchNumber }}.</strong>
      Delete the
      {{ highlightedTransferIds.length }} highlighted transfer(s) below, then go back to Production Entries and delete
      the batch. Each one takes its other products with it, so check the lines before deleting.
      @if (highlightedOnThisPage < highlightedTransferIds.length) {
        Only {{ highlightedOnThisPage }} of them are on this page: check the other pages or narrow the filters above.
      }
    </div>
    <button type="button" class="btn-close" (click)="clearBatchContext()" aria-label="Clear"></button>
  </div>
}

@if (deleteError) {
  <app-error-alert [error]="deleteError" [showFieldNames]="false" />
}

@if (isDataLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  @if (transfers.length === 0) {
    <app-info-alert>
      No product transfers found. Use the "New Transfer" button to move goods from factory to warehouse.
    </app-info-alert>
  } @else {
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="bg-light">
          <tr>
            <th class="border-0 py-3" style="width: 2.5rem"></th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Destination</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">From Factory</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Goods</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Transfer Date</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Invoice No.</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Invoice Date</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Vehicle No.</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Driver</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Transferred By</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (transfer of transfers; track transfer.id) {
            <tr
              [class.table-warning]="isHighlighted(transfer)"
              style="cursor: pointer"
              (click)="toggleExpanded(transfer)">
              <td class="py-3 text-center">
                <i
                  class="bi text-muted"
                  [class.bi-chevron-down]="isExpanded(transfer)"
                  [class.bi-chevron-right]="!isExpanded(transfer)"
                  [attr.aria-expanded]="isExpanded(transfer)"></i>
              </td>
              <td class="py-3">
                @if (transfer.warehouse_name) {
                  <span class="badge app-badge badge-soft-success">{{ transfer.destination_name }}</span>
                } @else if (transfer.dealer_name) {
                  <span class="badge app-badge badge-soft-warning">{{ transfer.destination_name }}</span>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3">
                <span class="badge app-badge badge-soft-primary">{{ transfer.factory_name }}</span>
              </td>
              <td class="py-3 text-center">
                <span class="fw-bold">{{ productCount(transfer.item_count) }}</span>
                <div class="text-muted small font-monospace">
                  {{ boxCount(transfer.total_box_quantity) }} ({{ transfer.total_quantity }} nos)
                </div>
              </td>
              <td class="py-3 text-center text-muted small">
                {{ transfer.transfer_date | date: 'mediumDate' }}
              </td>
              <td class="py-3 text-muted small font-monospace">{{ transfer.invoice_number || '—' }}</td>
              <td class="py-3 text-muted small">
                {{ transfer.invoice_date ? (transfer.invoice_date | date: 'mediumDate') : '—' }}
              </td>
              <td class="py-3 text-muted small font-monospace">{{ transfer.vehicle_number || '—' }}</td>
              <td class="py-3 text-muted small">{{ transfer.driver_name || '—' }}</td>
              <td class="py-3 text-muted small">{{ transfer.transferred_by_name || '—' }}</td>
              <td class="py-3 text-center">
                <button
                  type="button"
                  class="btn btn-action-delete"
                  title="Delete transfer"
                  (click)="$event.stopPropagation(); onDeleteTransfer(transfer)"
                  [disabled]="deletingTransferId === transfer.id">
                  @if (deletingTransferId === transfer.id) {
                    <span class="spinner-border spinner-border-sm"></span>
                  } @else {
                    <i class="bi bi-trash"></i>
                  }
                </button>
              </td>
            </tr>

            @if (isExpanded(transfer)) {
              <tr [class.table-warning]="isHighlighted(transfer)">
                <td class="p-0" [attr.colspan]="11">
                  <div class="px-4 py-3 bg-light-subtle border-top">
                    <table class="table table-sm mb-0 align-middle">
                      <thead>
                        <tr>
                          <th class="border-0 small fw-bold text-muted py-2">Product</th>
                          <th class="border-0 small fw-bold text-muted py-2">Packaging</th>
                          <th class="border-0 small fw-bold text-muted py-2">Batch No.</th>
                          <th class="border-0 small fw-bold text-muted py-2">Mfg Date</th>
                          <th class="border-0 small fw-bold text-muted py-2">Exp Date</th>
                          <th class="border-0 small fw-bold text-muted py-2 text-end">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (item of transfer.items; track item.id) {
                          <tr>
                            <td class="py-2 fw-semibold">{{ item.product_name }}</td>
                            <td class="py-2 text-muted small">{{ item.packaging_label }}</td>
                            <td class="py-2 font-monospace small">{{ item.batch_number || '—' }}</td>
                            <td class="py-2 text-muted small">
                              {{ item.manufacturing_date ? (item.manufacturing_date | date: 'mediumDate') : '—' }}
                            </td>
                            <td class="py-2 text-muted small">
                              {{ item.expiry_date ? (item.expiry_date | date: 'mediumDate') : '—' }}
                            </td>
                            <td class="py-2 text-end">
                              <span class="fw-bold font-monospace">{{ boxCount(item.box_quantity) }}</span>
                              <div class="text-muted small font-monospace">({{ item.quantity }} nos)</div>
                            </td>
                          </tr>
                        }
                      </tbody>
                      <tfoot>
                        <tr class="border-top">
                          <td colspan="5" class="py-2 text-end text-muted small fw-semibold">Transfer total</td>
                          <td class="py-2 text-end">
                            <span class="fw-bold font-monospace">{{ boxCount(transfer.total_box_quantity) }}</span>
                            <div class="text-muted small font-monospace">({{ transfer.total_quantity }} nos)</div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>

    @if (transfers.length > 0) {
      <div class="mt-4">
        <app-pagination
          [currentPage]="currentPage"
          [totalCount]="totalCount"
          [pageSize]="10"
          [hasNextPage]="hasNextPage"
          [hasPreviousPage]="hasPreviousPage"
          (pageChange)="onPageChange($event)"></app-pagination>
      </div>
    }
  }
}

<!-- Export Modal -->
<ng-template #exportModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Export Product Transfers</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    @if (exportError) {
      <app-error-alert [error]="exportError" class="mb-3"></app-error-alert>
    }
    <div class="row g-3">
      <div class="col-6">
        <label class="form-label">From Date</label>
        <input type="date" class="form-control" [(ngModel)]="exportFromDate" />
      </div>
      <div class="col-6">
        <label class="form-label">To Date</label>
        <input type="date" class="form-control" [(ngModel)]="exportToDate" />
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary btn-sm" (click)="modal.dismiss()" [disabled]="isExporting">
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
      (click)="onExportSubmit(modal)"
      [disabled]="isExporting">
      @if (isExporting) {
        <span class="spinner-border spinner-border-sm"></span>
        Exporting...
      } @else {
        <i class="bi bi-download"></i>
        Export
      }
    </button>
  </div>
</ng-template>

<ng-template #transferModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <form [formGroup]="transferForm" (ngSubmit)="onSubmitTransfer()">
      <div class="modal-header py-3">
        <h5 class="modal-title">New Product Transfer</h5>
        <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
      </div>

      <div class="modal-body p-4">
        @if (formError) {
          <app-error-alert [error]="formError" [showFieldNames]="false" />
        }

        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">
              From Factory
              <span class="text-danger">*</span>
            </label>
            <ng-select
              formControlName="factory"
              placeholder="Select factory..."
              [items]="factoryList"
              bindLabel="name"
              bindValue="id"></ng-select>
            @if (isInvalid('factory')) {
              <div class="text-danger small mt-1">Factory is required.</div>
            }
          </div>

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">
              Destination Type
              <span class="text-danger">*</span>
            </label>
            <div class="btn-group w-100" role="group">
              <input
                type="radio"
                class="btn-check"
                id="dest-warehouse"
                name="destType"
                [(ngModel)]="destinationType"
                [ngModelOptions]="{ standalone: true }"
                value="warehouse"
                (change)="onDestinationTypeChange()" />
              <label class="btn btn-soft-primary" for="dest-warehouse">
                <i class="bi bi-building me-1"></i>
                Warehouse
              </label>
              <input
                type="radio"
                class="btn-check"
                id="dest-dealer"
                name="destType"
                [(ngModel)]="destinationType"
                [ngModelOptions]="{ standalone: true }"
                value="dealer"
                (change)="onDestinationTypeChange()" />
              <label class="btn btn-soft-primary" for="dest-dealer">
                <i class="bi bi-person-badge me-1"></i>
                Dealer
              </label>
            </div>
            <div class="text-muted small mt-1">The whole consignment goes to one destination.</div>
          </div>

          @if (destinationType === 'warehouse') {
            <div class="col-md-6">
              <label class="form-label small fw-bold text-muted">
                To Warehouse
                <span class="text-danger">*</span>
              </label>
              <ng-select
                formControlName="warehouse"
                placeholder="Select warehouse..."
                [items]="warehouses"
                bindLabel="name"
                bindValue="id"></ng-select>
              @if (isInvalid('warehouse')) {
                <div class="text-danger small mt-1">Warehouse is required.</div>
              }
            </div>
          } @else {
            <div class="col-md-6">
              <label class="form-label small fw-bold text-muted">
                To Dealer
                <span class="text-danger">*</span>
              </label>
              <ng-select
                formControlName="dealer"
                placeholder="Select dealer..."
                [items]="dealers"
                bindLabel="shop_name"
                bindValue="id"></ng-select>
              @if (isInvalid('dealer')) {
                <div class="text-danger small mt-1">Dealer is required.</div>
              }
            </div>
          }

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">
              Transfer Date
              <span class="text-danger">*</span>
            </label>
            <input type="date" class="form-control" formControlName="transfer_date" />
            @if (isInvalid('transfer_date')) {
              <div class="text-danger small mt-1">Transfer date is required.</div>
            }
          </div>

          <div class="col-12"><hr class="my-1" /></div>

          <div class="col-12">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="form-label small fw-bold text-muted mb-0">
                Products
                @if (items.length > 1) {
                  <span class="badge bg-secondary-subtle text-secondary rounded-pill ms-1">{{ items.length }}</span>
                }
              </span>
              <button
                type="button"
                class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
                (click)="addItem()"
                [disabled]="!transferForm.get('factory')?.value">
                <i class="bi bi-plus-lg"></i>
                Add Product
              </button>
            </div>

            @if (!transferForm.get('factory')?.value) {
              <div class="text-muted small mb-2">Select a factory first to see the stock it holds.</div>
            }
            @if (isLoadingFactoryStock) {
              <div class="text-muted small mb-2">
                <span class="spinner-border spinner-border-sm me-1"></span>
                Loading stock...
              </div>
            }

            <div formArrayName="items" class="d-flex flex-column gap-2">
              @for (line of items.controls; track $index; let i = $index) {
                <div
                  [formGroupName]="i"
                  class="border rounded p-3 position-relative"
                  [class.border-danger]="lineErrors[i] || duplicateOfLine(i)">
                  @if (items.length > 1) {
                    <button
                      type="button"
                      class="btn btn-sm btn-link text-danger p-0 position-absolute"
                      style="top: 10px; right: 10px; line-height: 1"
                      title="Remove this product"
                      (click)="removeItem(i)">
                      <i class="bi bi-x-lg"></i>
                    </button>

                    <div class="extra-small fw-semibold text-muted text-uppercase mb-2" style="letter-spacing: 0.05em">
                      Line {{ i + 1 }}
                    </div>
                  }

                  <div class="row g-2">
                    <div class="col-md-5">
                      <label class="form-label small">
                        Product
                        <span class="text-danger">*</span>
                      </label>
                      <ng-select
                        formControlName="product_packaging"
                        placeholder="Select product from factory stock..."
                        [items]="factoryStockItems"
                        bindLabel="product_name"
                        bindValue="product_packaging"
                        [loading]="isLoadingFactoryStock"
                        [disabled]="!transferForm.get('factory')?.value">
                        <ng-template ng-label-tmp let-item="item">
                          {{ item.product_name }} — {{ item.packing_size }} {{ item.unit }} / {{ item.packaging_type }}
                        </ng-template>
                        <ng-template ng-option-tmp let-item="item">
                          <div>
                            <strong>{{ item.product_name }}</strong>
                            <span class="text-muted ms-2 small">
                              {{ item.packing_size }} {{ item.unit }} / {{ item.packaging_type }}
                            </span>
                          </div>
                          <div class="text-muted small">{{ item.units_available }} units available</div>
                        </ng-template>
                      </ng-select>
                      @if (isLineInvalid(i, 'product_packaging')) {
                        <div class="text-danger small mt-1">Product is required.</div>
                      }
                    </div>

                    <div class="col-md-4">
                      <label class="form-label small">
                        Batch
                        <span class="text-danger">*</span>
                      </label>
                      <ng-select
                        formControlName="production_entry"
                        placeholder="Select batch..."
                        [items]="lineBatchStocks[i]"
                        bindLabel="batch_number"
                        bindValue="production_entry"
                        [loading]="lineBatchLoading[i]"
                        [disabled]="!line.get('product_packaging')?.value">
                        <ng-template ng-option-tmp let-item="item">
                          <div class="d-flex justify-content-between gap-2">
                            <span class="fw-semibold font-monospace">{{ item.batch_number }}</span>
                            <span class="text-muted small font-monospace">{{ item.boxes_available }} boxes</span>
                          </div>
                          <div class="text-muted small">
                            Expires {{ item.expiry_date ? (item.expiry_date | date: 'mediumDate') : '—' }}
                          </div>
                        </ng-template>
                      </ng-select>
                      @if (
                        line.get('product_packaging')?.value && !lineBatchLoading[i] && lineBatchStocks[i].length === 0
                      ) {
                        <div class="text-warning small mt-1">No batches of this product left at the factory.</div>
                      }
                      @if (isLineInvalid(i, 'production_entry')) {
                        <div class="text-danger small mt-1">Batch is required.</div>
                      }
                    </div>

                    <div class="col-md-3">
                      <label class="form-label small">
                        Boxes
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        formControlName="box_quantity"
                        placeholder="Boxes"
                        min="1"
                        step="1" />
                      @if (selectedBatchFor(i); as batch) {
                        <div class="text-muted small mt-1 font-monospace">{{ batch.boxes_available }} available</div>
                      }
                      @if (isLineInvalid(i, 'box_quantity')) {
                        <div class="text-danger small mt-1">Whole boxes, at least 1.</div>
                      }
                    </div>
                  </div>

                  @if (duplicateOfLine(i); as duplicateLine) {
                    <div class="text-danger small mt-2">
                      <i class="bi bi-exclamation-triangle-fill me-1"></i>
                      This batch is already on line {{ duplicateLine }}. Put the whole quantity on that line instead.
                    </div>
                  }
                  @if (boxesOverAvailable(i) !== null) {
                    <div class="text-warning small mt-2">
                      <i class="bi bi-exclamation-triangle me-1"></i>
                      Only {{ boxesOverAvailable(i) }} boxes of this batch are at the factory. The transfer will be
                      rejected as it stands.
                    </div>
                  }
                  @if (lineErrors[i]) {
                    <div class="text-danger small mt-2">
                      <i class="bi bi-x-circle-fill me-1"></i>
                      {{ lineErrors[i] }}
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <div class="col-12"><hr class="my-1" /></div>

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">Invoice Number</label>
            <input type="text" class="form-control" formControlName="invoice_number" placeholder="e.g. INV-001" />
          </div>

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">Invoice Date</label>
            <input type="date" class="form-control" formControlName="invoice_date" />
          </div>

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">Vehicle Number</label>
            <input type="text" class="form-control" formControlName="vehicle_number" placeholder="e.g. MH-12-AB-1234" />
          </div>

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">Driver Name</label>
            <input type="text" class="form-control" formControlName="driver_name" placeholder="Driver's full name" />
          </div>
        </div>
      </div>

      <div class="modal-footer border-0 d-flex justify-content-between">
        <span class="text-muted small">
          {{ productCount(items.length) }}, {{ boxCount(totalBoxesOnForm) }} on this consignment
        </span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
          <button
            type="submit"
            class="btn btn-primary"
            [disabled]="transferForm.invalid || hasDuplicateBatchLine || isSaving">
            @if (isSaving) {
              <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Transferring...
            } @else {
              <i class="bi bi-arrow-left-right me-1"></i>
              Transfer
            }
          </button>
        </div>
      </div>
    </form>
  </div>
</ng-template>
